'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactNode, useState } from 'react'

// Default stale times for different data types (in milliseconds)
// INCREASED TO 30 MINUTES for better performance
export const STALE_TIMES = {
  // Data that rarely changes
  STATIC: 60 * 60 * 1000, // 60 minutes
  
  // User/clinic settings, staff lists
  SETTINGS: 30 * 60 * 1000, // 30 minutes
  
  // HR data, schedules, time entries
  HR_DATA: 30 * 60 * 1000, // 30 minutes
  
  // Lab results, referrals
  MEDICAL_RESULTS: 30 * 60 * 1000, // 30 minutes
  
  // Chat messages, notifications - need real-time feel
  REALTIME: 5 * 60 * 1000, // 5 minutes
  
  // Calendar appointments
  CALENDAR: 30 * 60 * 1000, // 30 minutes
  
  // Patient data
  PATIENT_DATA: 30 * 60 * 1000, // 30 minutes
  
  // Clinic data (staff lists, settings)
  CLINIC_DATA: 30 * 60 * 1000, // 30 minutes
}

// Cache times (how long to keep data in cache after it becomes inactive)
export const CACHE_TIMES = {
  SHORT: 15 * 60 * 1000, // 15 minutes
  MEDIUM: 30 * 60 * 1000, // 30 minutes
  LONG: 60 * 60 * 1000, // 60 minutes (1 hour)
}

export default function QueryProvider({ children }: { children: ReactNode }) {
  // Create QueryClient inside component to avoid sharing state between requests
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // How long until data is considered stale (default: 30 minutes)
            staleTime: 30 * 60 * 1000,
            
            // How long to keep inactive data in cache (default: 60 minutes)
            gcTime: 60 * 60 * 1000,
            
            // Retry failed requests 2 times
            retry: 2,
            
            // Don't refetch on window focus for less noise
            refetchOnWindowFocus: false,
            
            // Don't refetch when component remounts if data is fresh
            refetchOnMount: false,
            
            // Keep previous data while fetching new data
            placeholderData: (previousData: unknown) => previousData,
          },
          mutations: {
            // Retry mutations once
            retry: 1,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}
